// src/contexts/LocaleContext.js
import { useState, createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import en from '../locales/en.json';
import ja from '../locales/ja.json';

const LOCAL_STORAGE_KEY = 'app-locale';

const resources = {
  en,
  ja: { ...en, ...ja },
};

export const SUPPORTED_LOCALES = Object.keys(resources);

export const LocaleContext = createContext();

export const useLocale = () => {
  return useContext(LocaleContext);
};

export const LocaleProvider = ({ children }) => {
  const getInitialLocale = () => {
    const savedLocale = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedLocale && SUPPORTED_LOCALES.includes(savedLocale)) {
      return savedLocale;
    }

    const browserLang = navigator.language.split('-')[0];
    if (SUPPORTED_LOCALES.includes(browserLang)) {
      return browserLang;
    }
    
    return 'ja';
  };

  const [locale, setLocale] = useState(getInitialLocale);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, locale);
  }, [locale]);

  const t = useCallback((key, options) => {
    let translation = resources[locale]?.[key] ?? resources['en']?.[key] ?? key;

    if (options && typeof translation === 'string') {
      for (const placeholderKey in options) {
        translation = translation.replace(new RegExp(`\\{${placeholderKey}\\}`, 'g'), options[placeholderKey]);
      }
    }
    return translation;
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t
  }), [locale, t]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};